import React, { useState, useRef } from "react";
import {
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Select,
  Upload,
  Image,
  Modal,
  notification,
  Spin,
  Button,
} from "antd";
import {
  PlusOutlined,
  LoadingOutlined,
  PictureOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  _create_menu_restaurant,
  _upload_any_file,
  _update_menu,
  _get_category_presets,
} from "../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../common/utils/notification";

const { Option } = Select;

const RestaurantMenuForm = ({
  selectedMenu,
  categories,
  drawerVisibility,
  callback,
  user,
  navigate,
}) => {
  const [form] = Form.useForm();
  const customFileInputRef = useRef(null);
  const [addingMenu, setAddingMenu] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [categoryPresets, setCategoryPresets] = useState([]);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [allCategoryPresets, setAllCategoryPresets] = useState({});
  const [activeCategoryTab, setActiveCategoryTab] = useState("Beans");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deliveryDateType, setDeliveryDateType] = useState("same_day");
  const [packType, setPackType] = useState("1_litre");
  const [spicyLevel, setSpicyLevel] = useState("mild");
  const [price, setPrice] = useState("");

  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );

  const handlePriceChange = (value) => {
    let numericValue = value.replace(/[^\d.]/g, "");
    setPrice(numericValue);
    form.setFieldsValue({ price: numericValue });
  };

  const openNotificationWithIcon = (type, title, message) => {
    notification[type]({
      message: title,
      description: message,
      placement: "bottom-right",
    });
  };

  const handlePriceBlur = () => {
    if (price) {
      const formattedPrice = parseFloat(price).toFixed(2);
      setPrice(formattedPrice);
      form.setFieldsValue({ price: formattedPrice });
    }
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const changeImage = ({ fileList: newFileList }) => setFileList(newFileList);

  const handleRemoveImage = async (file) => {
    if (selectedMenu) {
      openNotificationWithIcon(
        "info",
        "Image removal not supported by backend",
        "Please update the menu to remove images.",
      );
    }
  };

  const onFinish = async (values) => {
    try {
      setAddingMenu(true);
      let imageUrl = selectedMenu?.imageUrl || selectedMenu?.image_url || null;
      if (fileList.length > 0) {
        const fileToUpload = fileList[0].originFileObj;
        if (fileToUpload) {
          const response = await _upload_any_file(fileToUpload);
          imageUrl =
            response.data?.url ||
            response.data?.data?.url ||
            response.data?.path ||
            response.data?.data?.path ||
            null;
        } else if (fileList[0].url) {
          imageUrl = fileList[0].url;
        } else if (fileList[0].thumbUrl) {
          imageUrl = fileList[0].thumbUrl;
        }
      }

      const data = {
        name: values.menuName,
        description: values.description || "",
        price: parseFloat(values.price),
        categoryId: values.category,
        openingStock: values.opening_stock || 0,
        imageUrl,
        deliveryDateType,
        packType,
        containerCost: 0,
        portionsPerPack: 1,
      };

      let response;
      if (selectedMenu) {
        response = await _update_menu(selectedMenu.id, data);
        openNotificationWithIcon(
          "success",
          response?.data?.title,
          response?.data?.message,
        );
      } else {
        response = await _create_menu_restaurant(data);
        showSuccessNotification(
          response?.data?.message || "Menu created successfully",
        );
      }
      form.resetFields();
      drawerVisibility();
      setFileList([]);
      callback();
    } catch (err) {
      if (err.response) {
        if (err.response.data.message === "Unauthorized") {
          localStorage.removeItem("konnect_token");
          navigate("/signin");
        }
        showErrorNotification(err.response.data.message);
      } else {
        openNotificationWithIcon("error", err.message);
      }
    } finally {
      setAddingMenu(false);
    }
  };

  React.useEffect(() => {
    if (selectedMenu) {
      if (selectedMenu.imageUrl) {
        const mappedFileList = [
          {
            uid: "1",
            name: "image",
            status: "done",
            thumbUrl: selectedMenu.imageUrl.split("?")[0],
            url: selectedMenu.imageUrl.split("?")[0],
          },
        ];
        setFileList(mappedFileList);
      } else {
        setFileList([]);
      }

      form.setFieldsValue({
        menuName: selectedMenu.name,
        price: selectedMenu.price,
        category: selectedMenu.category?.id,
        description: selectedMenu.description,
        opening_stock: selectedMenu.openingStock,
      });
      setDeliveryDateType(selectedMenu.deliveryDateType || "same_day");
      setPackType(selectedMenu.packType || "1_litre");
      setSpicyLevel(selectedMenu.spicyLevel || "mild");

      if (selectedMenu.category?.name || selectedMenu.category?.id) {
        const catName = selectedMenu.category.name || selectedMenu.category.id;
        _get_category_presets(catName)
          .then((res) => setCategoryPresets(res?.data || []))
          .catch(() => setCategoryPresets([]));
      }
    }
  }, [selectedMenu, form]);
  const getPresetsForCategory = (presetsObj, categoryKey) => {
    if (!presetsObj) return [];
    const targetObj =
      presetsObj?.data &&
        typeof presetsObj.data === "object" &&
        !Array.isArray(presetsObj.data)
        ? presetsObj.data
        : presetsObj;
    if (Array.isArray(targetObj)) return targetObj;
    if (targetObj[categoryKey] && Array.isArray(targetObj[categoryKey])) {
      return targetObj[categoryKey];
    }
    const matchedKey = Object.keys(targetObj || {}).find(
      (k) => k.toLowerCase() === (categoryKey || "").toLowerCase(),
    );
    if (matchedKey && Array.isArray(targetObj[matchedKey])) {
      return targetObj[matchedKey];
    }
    return [];
  };

  const getCategoryKeys = (presetsObj) => {
    if (!presetsObj) return [];
    const targetObj =
      presetsObj?.data &&
        typeof presetsObj.data === "object" &&
        !Array.isArray(presetsObj.data)
        ? presetsObj.data
        : presetsObj;
    if (Array.isArray(targetObj)) return [];
    return Object.keys(targetObj || {}).filter((k) =>
      Array.isArray(targetObj[k]),
    );
  };

  const openStockPhotoModal = async () => {
    try {
      const res = await _get_category_presets("");
      const presetData = res?.data?.data || res?.data || {};
      setAllCategoryPresets(presetData);
      const keys = getCategoryKeys(presetData);
      if (keys.length > 0) {
        const currentCatId = form.getFieldValue("category");
        const selectedCat = categories.find(
          (c) => c.id === currentCatId || c.name === currentCatId,
        );
        const catName = selectedCat?.name || "Beans";
        const matchingKey = keys.find(
          (k) => k.toLowerCase() === catName.toLowerCase(),
        );
        setActiveCategoryTab(matchingKey || keys[0]);
      }
    } catch (err) {
      console.error(err);
    }
    setStockModalOpen(true);
  };

  const uploadButton = (
    <button
      style={{ border: 0, background: "none" }}
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openStockPhotoModal();
      }}
    >
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  const handleCategorySelect = async (catId) => {
    form.setFieldsValue({ category: catId });
    const selectedCat = categories.find(
      (c) => c.id === catId || c.name === catId,
    );
    const catName = selectedCat?.name || catId || "";
    if (catName) {
      try {
        const res = await _get_category_presets(catName);
        const presetsArr = getPresetsForCategory(res?.data, catName);
        setCategoryPresets(presetsArr);
      } catch (err) {
        setCategoryPresets([]);
      }
    } else {
      setCategoryPresets([]);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <input
        type="file"
        ref={customFileInputRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            const customFile = {
              uid: `custom-${Date.now()}`,
              name: file.name,
              status: "done",
              originFileObj: file,
              url: URL.createObjectURL(file),
            };
            setFileList([customFile]);
            setStockModalOpen(false);
            openNotificationWithIcon(
              "success",
              "File Uploaded",
              "Custom device image selected successfully!",
            );
          }
        }}
      />

      <Modal
        title={
          <div className="flex items-center gap-2">
            <PictureOutlined className="text-gray-800 text-lg" />
            <span className="font-gilroyBold text-base sm:text-lg text-gray-900">
              Select Menu Image
            </span>
          </div>
        }
        open={stockModalOpen}
        onCancel={() => setStockModalOpen(false)}
        centered
        width="92%"
        style={{ maxWidth: "580px" }}
        bodyStyle={{ maxHeight: "75vh", overflowY: "auto", padding: "14px" }}
        footer={[
          <div
            key="actions"
            className="flex justify-between items-center flex-wrap gap-2"
          >
            <Button
              icon={<UploadOutlined />}
              onClick={() => customFileInputRef.current?.click()}
              className="bg-gray-100 text-gray-800 border-gray-300 rounded-xl h-10 text-[13px] font-gilroyBold"
            >
              Upload from Device
            </Button>
            <Button
              type="primary"
              onClick={() => setStockModalOpen(false)}
              className="bg-black hover:bg-gray-800 text-white border-none rounded-xl h-10 px-5 text-[14px] font-gilroyBold"
            >
              Done
            </Button>
          </div>,
        ]}
      >
        <div className="space-y-3 pt-1">
          <div
            className="flex gap-2 border-b border-gray-200 pb-2.5 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {getCategoryKeys(allCategoryPresets).map((catName) => {
              const count = getPresetsForCategory(
                allCategoryPresets,
                catName,
              ).length;
              return (
                <button
                  key={catName}
                  type="button"
                  onClick={() => setActiveCategoryTab(catName)}
                  className={`px-3 py-1.5 rounded-xl text-[13px] font-gilroyBold whitespace-nowrap transition-all shrink-0 ${activeCategoryTab === catName
                      ? "bg-black text-white border border-black"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {catName} ({count})
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 pt-1">
            {getPresetsForCategory(allCategoryPresets, activeCategoryTab).map(
              (presetUrl, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    const newPresetFile = {
                      uid: `preset-${idx}`,
                      name: `${activeCategoryTab} Stock Photo ${idx + 1}`,
                      status: "done",
                      url: presetUrl,
                    };
                    setFileList([newPresetFile]);
                    form.setFieldsValue({ image: presetUrl });
                    setStockModalOpen(false);
                    openNotificationWithIcon(
                      "success",
                      "Stock Photo Selected",
                      "Category stock photo selected successfully!",
                    );
                  }}
                  className="aspect-square rounded-xl overflow-hidden cursor-pointer border-2 border-gray-200 hover:border-black transition-all relative group shadow-sm bg-gray-100"
                >
                  <img
                    src={presetUrl}
                    alt={`${activeCategoryTab} ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
              ),
            )}
          </div>
        </div>
      </Modal>

      <Form.Item
        label="Upload Menu Images"
        rules={[{ required: true, message: "Kindly add the menu image" }]}
      >
        <Upload
          listType="picture-card"
          fileList={fileList}
          onPreview={handlePreview}
          onChange={changeImage}
          onRemove={(file) => handleRemoveImage(file)}
          openFileDialogOnClick={false}
        >
          {fileList.length >= 1 ? null : uploadButton}
        </Upload>
        {previewImage && (
          <Image
            wrapperStyle={{ display: "none" }}
            preview={{
              visible: previewOpen,
              onVisibleChange: (visible) => setPreviewOpen(visible),
              afterOpenChange: (visible) => !visible && setPreviewImage(""),
            }}
            src={previewImage}
          />
        )}
        <div>
          <Modal
            open={modalOpen}
            footer={null}
            onCancel={() => setModalOpen(false)}
          >
            <img
              src="/images/dish-container.jpg"
              alt="dish container"
              className="m-auto w-full h-full"
            />
          </Modal>
          <div className="flex flex-col gap-1">
            <span
              className="text-gray-900 font-bold hover:cursor-pointer"
              onClick={() => setModalOpen(true)}
            >
              Price below must be inclusive of all packaging costs and covers
              the item exactly as described
            </span>
          </div>
        </div>
      </Form.Item>

      <Form.Item
        name="category"
        label="Select Category"
        rules={[{ required: true, message: "Please select a category!" }]}
      >
        <Select placeholder="Select category" onChange={handleCategorySelect}>
          {categories.map((category) => (
            <Option key={category.id} value={category.id}>
              {category.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {categoryPresets.length > 0 && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-2xl space-y-2">
          <span className="text-[13px] font-gilroyBold text-gray-700 block">
            Or choose a stock photo for this category:
          </span>
          <div
            className="flex gap-2 overflow-x-auto pb-1 scroll-smooth [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {categoryPresets.map((presetUrl, idx) => (
              <div
                key={idx}
                onClick={() => {
                  const newPresetFile = {
                    uid: `preset-${idx}`,
                    name: `Stock Photo ${idx + 1}`,
                    status: "done",
                    url: presetUrl,
                  };
                  setFileList([newPresetFile]);
                  openNotificationWithIcon(
                    "success",
                    "Stock Photo Selected",
                    "Category stock photo selected successfully!",
                  );
                }}
                className="w-16 h-16 rounded-xl overflow-hidden cursor-pointer border-2 border-gray-200 hover:border-black transition-all shrink-0 relative group shadow-sm"
              >
                <img
                  src={presetUrl}
                  alt={`Stock Preset ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <Form.Item
        label="Menu Name"
        name="menuName"
        rules={[{ required: true, message: "Please fill input is required" }]}
      >
        <Input placeholder="Enter menu name" />
      </Form.Item>

      <Form.Item
        label="Delivery Date Type"
        rules={[
          { required: true, message: "Please select delivery date type" },
        ]}
      >
        <Select
          value={deliveryDateType}
          onChange={setDeliveryDateType}
          placeholder="Select delivery date type"
        >
          <Option value="same_day">Same Day</Option>
          <Option value="next_day">Next Day</Option>
        </Select>
      </Form.Item>

      {/* <Form.Item
        label="Pack Type"
        rules={[{ required: true, message: "Please select pack type" }]}
      >
        <Select
          value={packType}
          onChange={setPackType}
          placeholder="Select pack type"
        >
          <Option value="1_litre">1 litre</Option>
          <Option value="2_litres">2 litres</Option>
          <Option value="wrap_foil">Wrap/Foil</Option>
          <Option value="na">Not applicable</Option>
        </Select>
      </Form.Item> */}

      <Form.Item
        label="Spicy Level"
        rules={[{ required: true, message: "Please select spicy level" }]}
      >
        <Select
          value={spicyLevel}
          onChange={setSpicyLevel}
          placeholder="Select spicy level"
        >
          <Option value="mild">Mild</Option>
          <Option value="hot">Hot</Option>
          <Option value="na">Not Applicable</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Opening Stock"
        name="opening_stock"
        rules={[{ required: true, message: "Please fill input is required" }]}
      >
        <InputNumber
          min={0}
          className="w-full"
          type="number"
          inputMode="numeric"
          placeholder="Enter extra opening stock"
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Price"
            name="price"
            rules={[
              { required: true, message: "Please fill input is required" },
            ]}
          >
            <Input
              prefix="NGN"
              value={price}
              onChange={(e) => handlePriceChange(e.target.value)}
              onBlur={handlePriceBlur}
              type="number"
              inputMode="numeric"
              placeholder="Enter price"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Description" name="description">
        <Input.TextArea placeholder="Description" rows={3} />
      </Form.Item>

      <div className={"flex justify-between"}>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={drawerVisibility}
            className="menu-update-btn bg-gray-500 text-white hover:bg-gray-600 border-none px-6 py-3 rounded-xl tracking-wider transition-colors"
          >
            Back
          </button>
          {addingMenu ? (
            <button
              className="menu-update-btn bg-gray-300 hover:bg-gray-300 hover:text-gray-500 text-gray-500 cursor-not-allowed border-none px-10 py-3 rounded-xl flex items-center justify-center tracking-wider transition-colors"
              disabled
            >
              {selectedMenu ? "Editing Item" : "Adding Item"}{" "}
              <Spin
                style={{ marginLeft: "10px" }}
                indicator={newAntIcon}
              />{" "}
            </button>
          ) : (
            <button
              type="submit"
              className="menu-update-btn bg-gray-900 text-white hover:bg-gray-800 border-none px-10 py-3 rounded-xl tracking-wider transition-colors"
            >
              {selectedMenu ? "Edit Item" : "Add Item"}
            </button>
          )}
        </div>
      </div>
    </Form>
  );
};

export default RestaurantMenuForm;
